import { AvailabilityType } from "@features/eventPage/types";
import { EventShowing, ShowingMessage } from "@types";
import { AccessType, ShowingStatus } from "@utils/enums";
import { format, parseISO } from "date-fns";
import { useTranslations } from "next-intl";

const handleShowingStatus = (
  statusId: number,
  messages: ShowingMessage[],
  isShopsite: boolean
) => {
  const buttonMessageShowing =
    messages
      ?.filter((msg) => msg.type === "button")
      .find((msg) => msg.status === statusId)?.message ?? "";

  const messageShowing =
    messages
      ?.filter((msg) => msg.type === "message")
      .find((msg) => msg.status === statusId)?.message ?? "";

  if (
    statusId === ShowingStatus.SOLDOUT ||
    statusId === ShowingStatus.ADMINSOLDOUT ||
    statusId === ShowingStatus.CANCELLED
  ) {
    return {
      bgColor: isShopsite ? "bg-red-100" : "bg-[color:var(--red-a3)]",
      textColor: isShopsite ? "text-red-600" : "text-[color:var(--red-a11)]",
      title: buttonMessageShowing,
      descriptionMsg: messageShowing,
      selectable: false,
    };
  }

  if (
    statusId === ShowingStatus.POSTPONED ||
    statusId === ShowingStatus.NOTYETONSALE
  ) {
    return {
      bgColor: isShopsite ? "bg-orange-100" : "bg-[color:var(--orange-a3)]",
      textColor: isShopsite
        ? "text-orange-600"
        : "text-[color:var(--orange-a11)]",
      title: buttonMessageShowing,
      descriptionMsg: messageShowing,
      selectable: false,
    };
  }

  return {
    title: buttonMessageShowing,
    descriptionMsg: messageShowing,
    bgColor: isShopsite ? "bg-indigo-100" : "bg-[color:var(--accent-a3)]",
    textColor: isShopsite
      ? "text-indigo-600"
      : "text-[color:var(--accent-a11)]",
  };
};

export const formatShowings = (
  showings: EventShowing[],
  messages: ShowingMessage[],
  isShopsite: boolean
) => {
  return Object.values(
    showings?.reduce(
      (acc, obj) => ({
        ...acc,
        [format(parseISO(obj.date.local.start), `d MMM yyyy`)]: obj,
      }),
      {} as EventShowing[]
    )
  ).map((showing) => {
    let isClickable = false;

    // if there is AT LEAST one available showing status and a mix of other showing status then make it clickable
    // if there is NO available showing status and a mix of other showings make it clickable
    // if there IS NOT available showing status and THE REST OF The showing statuses are the same, then not clickable

    // Create a set to store unique status ids
    const statusSet = new Set<number>();
    let all_hot = true;

    // Loop through all showings for the same date
    for (const s of showings.filter(
      (s) =>
        s.active &&
        format(parseISO(s.date.local.start), `d MMM yyyy`) ===
          format(parseISO(showing.date.local.start), `d MMM yyyy`)
    )) {
      const status = s.status.id;

      if (status === ShowingStatus.AVAILABLE) {
        isClickable = true;
      }

      if (!s.status.hot) {
        all_hot = false;
      }

      statusSet.add(status);
    }

    // If there's at least one "AVAILABLE" status or a mix of other statuses, make it clickable
    if (statusSet.size > 1) {
      isClickable = true;
    }

    const status =
      showings
        ?.filter(
          (s) =>
            s.active &&
            format(parseISO(s.date.local.start), `d MMM yyyy`) ===
              format(parseISO(showing.date.local.start), `d MMM yyyy`)
        )
        .find((s) => s.status.id === ShowingStatus.AVAILABLE)?.status ||
      showing.status;

    return {
      id: showing.id,
      datename: format(parseISO(showing.date.local.start), `EEE d MMM yyyy`),
      day: format(parseISO(showing.date.local.start), `d`),
      displayDate: showing.date.display,
      shows: showings?.filter(
        (s) =>
          s.active &&
          format(parseISO(s.date.local.start), `d MMM yyyy`) ===
            format(parseISO(showing.date.local.start), `d MMM yyyy`)
      ).length,
      access: showing.access,
      status: {
        ...status,
        ...handleShowingStatus(status.id, messages, isShopsite),
        hot: all_hot,
      },
      isClickable,
    };
  });
};

export const handleAccessType = (
  time: EventShowing,
  messages: ShowingMessage[] | undefined,
  statusTranslations: ReturnType<typeof useTranslations<"EventPage.statuses">>
): AvailabilityType | undefined => {
  const titleShowing =
    messages
      ?.filter((msg) => msg.type === "button")
      .find((msg) => msg.status === time.status.id)?.message ?? "";

  const titleDescription =
    messages
      ?.filter((msg) => msg.type === "message")
      .find((msg) => msg.status === time.status.id)?.message ?? "";
  if (time.status.id === ShowingStatus.POSTPONED) {
    return {
      icon: "warning",
      bgColor: "bg-orange-100",
      textColor: "text-orange-600",
      title:
        titleShowing.trim() ||
        statusTranslations("postponed.heading") ||
        "Postponed",
      description:
        titleDescription.trim() ||
        statusTranslations("postponed.description") ||
        "This has been postponed.",
      selectable: false,
    };
  }

  if (time.status.id === ShowingStatus.NOTYETONSALE) {
    return {
      icon: "warning",
      bgColor: "bg-orange-100",
      textColor: "text-orange-600",
      title:
        titleShowing.trim() ||
        statusTranslations("notonsale.heading") ||
        "Not on sale",
      description:
        titleDescription.trim() ||
        statusTranslations("notonsale.description") ||
        "Not on sale.",
      selectable: false,
    };
  }

  if (
    time.status.id === ShowingStatus.SOLDOUT ||
    time.status.id === ShowingStatus.ADMINSOLDOUT
  ) {
    return {
      icon: "closed",
      bgColor: "bg-red-100",
      textColor: "text-red-600",
      title:
        titleShowing.trim() ||
        statusTranslations("soldout.heading") ||
        "Sold out",
      description:
        titleDescription.trim() ||
        statusTranslations("soldout.description") ||
        "This showing is sold out.",
      selectable: false,
    };
  }

  if (time.status.id === ShowingStatus.CANCELLED) {
    return {
      icon: "closed",
      bgColor: "bg-red-100",
      textColor: "text-red-600",
      title:
        titleShowing.trim() ||
        statusTranslations("cancelled.heading") ||
        "Cancelled",
      description:
        titleDescription.trim() ||
        statusTranslations("cancelled.description") ||
        "This showing is cancelled.",
      selectable: false,
    };
  }

  if (time.status.id === ShowingStatus.FREEENTRY) {
    return {
      icon: "closed",
      bgColor: "bg-green-100",
      textColor: "text-green-600",
      title:
        titleShowing.trim() ||
        statusTranslations("freeentry.heading") ||
        "Free Entry",
      description:
        titleDescription.trim() ||
        statusTranslations("freeentry.description") ||
        "This is a free entry event. You do not need a ticket.",
      selectable: false,
    };
  }

  if (time.status.id === ShowingStatus.AVAILABLE) {
    const title = messages
      ?.filter((msg) => msg.type === "button")
      .find((msg) => msg.access === time.access.type)?.message;

    const description = messages
      ?.filter((msg) => msg.type === "message")
      .find((msg) => msg.access === time.access.type)?.message;
    switch (time.access.type) {
      case AccessType.PUBLIC:
        return time.status.hot === 1
          ? {
              icon: "fire",
              bgColor: "bg-red-100",
              textColor: "text-red-600",
              selectable: true,
            }
          : {
              icon: "check",
              bgColor: "bg-green-100",
              textColor: "text-green-600",
              selectable: true,
            };
      case AccessType.PASSWORD:
        return {
          icon: "shield",
          bgColor: "bg-green-100",
          textColor: "text-green-600",
          selectable: true,
        };
      case AccessType.NOTYETONSALE || AccessType.NOTONSALE:
        return {
          icon: "warning",
          bgColor: "bg-orange-100",
          textColor: "text-orange-600",
          title: title
            ? title.replace(
                "{date}",
                time.access.date.public.local.start.formatted ??
                  format(
                    parseISO(time.access.date.public.local.start.raw),
                    `EEE d MMM yyyy h:mm aa`
                  )
              )
            : statusTranslations("notyetonsale.heading") || "Not on sale",
          description: description
            ? description.replace(
                "{date}",
                time.access.date.public.local.start.formatted ??
                  format(
                    parseISO(time.access.date.public.local.start.raw),
                    `EEE d MMM yyyy h:mm aa`
                  )
              )
            : statusTranslations("notyetonsale.description") ||
              "Not yet on sale",
          selectable: false,
        };
      case AccessType.SALESCLOSED:
        return {
          icon: "closed",
          bgColor: "bg-red-100",
          textColor: "text-red-600",
          title:
            title ||
            statusTranslations("salesclosed.heading") ||
            "Sales closed.",
          description:
            description ||
            statusTranslations("salesclosed.description") ||
            "Sales closed.",
          selectable: false,
        };
      case AccessType.DOORSALES:
        return {
          icon: "warning",
          bgColor: "bg-orange-100",
          textColor: "text-orange-600",
          title:
            title || statusTranslations("doorsales.heading") || "Door Sales",
          description:
            description ||
            statusTranslations("doorsales.description") ||
            "Door sales may still be available.",
          selectable: false,
        };
      case AccessType.EXPIRED:
        return {
          icon: "closed",
          bgColor: "bg-red-100",
          textColor: "text-red-600",
          title: title || statusTranslations("expired.heading") || "Expired",
          description:
            description ||
            statusTranslations("expired.description") ||
            "The showing at this time has expired.",
          selectable: false,
        };
    }
  }
};

export const formatTime = (time: string) => {
  const [hour, minute] = time.split(":");
  const isPM = time.toLowerCase().includes("pm") && parseInt(hour) !== 12;
  return {
    hour: parseInt(hour),
    minute: parseInt(minute),
    isPM,
  };
};
